// ---------------------------------------------------------------------------
// AudioPanel — Per-question translation & audio management dialog
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { Volume2, Globe, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { AudioPlayer } from './AudioPlayer';
import { AudioUploader } from './AudioUploader';
import { useTranslations, useAddTranslation } from '@/hooks/use-audio';

const LANGUAGES = [
  { code: 'es', label: 'Spanish' },
  { code: 'zh', label: 'Chinese' },
  { code: 'fr', label: 'French' },
  { code: 'ko', label: 'Korean' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'ru', label: 'Russian' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ht', label: 'Haitian Creole' },
  { code: 'pt', label: 'Portuguese' },
];

interface AudioPanelProps {
  questionId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AudioPanel({ questionId, open, onOpenChange }: AudioPanelProps) {
  const { toast } = useToast();
  const { data: translations, isLoading, refetch } = useTranslations(questionId);
  const addTranslation = useAddTranslation(questionId);

  const [showAdd, setShowAdd] = useState(false);
  const [newLang, setNewLang] = useState('');
  const [newText, setNewText] = useState('');

  const existingLangs = new Set(translations?.map((t) => t.language_code) ?? []);
  const availableLangs = LANGUAGES.filter((l) => !existingLangs.has(l.code));

  async function handleAddTranslation() {
    if (!newLang || !newText.trim()) return;
    try {
      await addTranslation.mutateAsync({
        languageCode: newLang,
        translatedText: newText.trim(),
      });
      setShowAdd(false);
      setNewLang('');
      setNewText('');
      toast({ title: 'Translation added', variant: 'success' });
    } catch {
      toast({ title: 'Failed to add translation', variant: 'destructive' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-purple-500" />
            Translations & Audio
          </DialogTitle>
          <DialogDescription>
            Manage translations and audio recordings for question #{questionId}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : translations && translations.length > 0 ? (
            translations.map((t) => (
              <div
                key={t.translation_id}
                className="rounded-ds-008 border border-silver-200 p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{t.language_code.toUpperCase()}</Badge>
                    <span className="text-xs text-carbon-500">
                      {LANGUAGES.find((l) => l.code === t.language_code)?.label ?? t.language_code}
                    </span>
                  </div>
                  {t.audio_file_key ? (
                    <Volume2 className="h-4 w-4 text-purple-500" />
                  ) : (
                    <Volume2 className="h-4 w-4 text-silver-300" />
                  )}
                </div>

                <p className="text-sm text-carbon-800">{t.translated_text}</p>

                {t.audio_file_key ? (
                  <AudioPlayer s3Key={t.audio_file_key} />
                ) : (
                  <AudioUploader
                    questionId={questionId}
                    languageCode={t.language_code}
                    onComplete={() => refetch()}
                  />
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-carbon-400 italic text-center py-4">
              No translations yet.
            </p>
          )}

          {/* Add translation form */}
          {showAdd ? (
            <div className="rounded-ds-008 border border-purple-200 bg-purple-50/30 p-3 space-y-3">
              <div>
                <Label className="mb-1">Language</Label>
                <select
                  value={newLang}
                  onChange={(e) => setNewLang(e.target.value)}
                  className="flex h-10 w-full rounded-ds-004 border border-silver-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Select language...</option>
                  {availableLangs.map((l) => (
                    <option key={l.code} value={l.code}>{l.label} ({l.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="mb-1">Translated Text</Label>
                <Textarea
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  placeholder="Enter the translated question text..."
                  className="min-h-[60px]"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddTranslation}
                  disabled={!newLang || !newText.trim() || addTranslation.isPending}
                >
                  {addTranslation.isPending ? 'Adding...' : 'Add Translation'}
                </Button>
              </div>
            </div>
          ) : (
            availableLangs.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => setShowAdd(true)}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Translation
              </Button>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
